import { deriveFocusSkills } from './skillMapping';
import { getAnalysisSignals, normalizeReviewAnalysis } from './reviewAnalysis';

// Pass 77 — the snowboard AI-rationale term maps / key sets were REMOVED: AI is non-authoritative for
// progression. This aggregate is now built exclusively from HUMAN structured `video.analysis` signals and human
// review drills/focus skills, and is used for display context (recurring themes) — never for the canonical
// progression ranking (progressionEvidence.js).

function incrementCount(map, key, payload = {}) {
  if (!key) return;
  const current = map.get(key) || {
    key,
    label: payload.label || key,
    count: 0,
    lastSeenAt: payload.lastSeenAt || 0,
  };
  current.count += payload.amount || 1;
  current.lastSeenAt = Math.max(current.lastSeenAt || 0, payload.lastSeenAt || 0);
  if (payload.label) current.label = payload.label;
  map.set(key, current);
}

function toSortedList(map, limit = 4) {
  return Array.from(map.values())
    .sort((a, b) => b.count - a.count || b.lastSeenAt - a.lastSeenAt || a.label.localeCompare(b.label))
    .slice(0, limit);
}

function normalizeSeconds(value) {
  return value?.seconds || 0;
}

function getReviewTimestamp(video) {
  return normalizeSeconds(video?.reviewedAt) || normalizeSeconds(video?.createdAt) || 0;
}

function buildCurrentLevelSummary(latestReview, aggregate) {
  if (!latestReview) return '';

  const analysis = normalizeReviewAnalysis(latestReview, latestReview.analysis);
  const sport = analysis?.sport || latestReview?.sport || '';
  if (sport === 'skiing' && analysis?.variant === 'sas_ski_analysis_v1') {
    const terrain = analysis.detect?.terrainComfort || 'current terrain';
    const turnType = analysis.detect?.turnType || 'skiing';
    const mainGoal = aggregate.developmentGoals[0]?.label || aggregate.focusAreas[0]?.label || 'cleaner movement patterns';
    return `Developing ${turnType.toLowerCase()} on ${terrain.toLowerCase()} with current focus on ${mainGoal.toLowerCase()}.`;
  }

  if (sport === 'snowboarding' && analysis?.variant === 'sas_snowboard_rider_analysis_v1') {
    const turnSide = analysis.detect?.turnSide || 'both turns';
    const mainGoal = aggregate.developmentGoals[0]?.label || aggregate.focusAreas[0]?.label || 'smoother board performance';
    const terrainContext = aggregate.terrainComfort.bestKnown ? ` on ${aggregate.terrainComfort.bestKnown.toLowerCase()}` : '';
    return `Building more consistent control through ${turnSide.toLowerCase()}${terrainContext} with current focus on ${mainGoal.toLowerCase()}.`;
  }

  return '';
}

function buildTrendSignals(reviewSignals = []) {
  if (reviewSignals.length < 2) {
    return {};
  }

  const midpoint = Math.max(1, Math.floor(reviewSignals.length / 2));
  const recent = reviewSignals.slice(0, midpoint);
  const older = reviewSignals.slice(midpoint);

  const countGroup = (entries, keys) =>
    entries.reduce((total, entry) => total + keys.filter((key) => entry.issueKeys.includes(key) || entry.goalKeys.includes(key) || entry.strengthKeys.includes(key)).length, 0);

  const describeTrend = (recentCount, olderCount) => {
    if (recentCount === 0 && olderCount === 0) return '';
    if (recentCount < olderCount) return 'improving';
    if (recentCount > olderCount) return 'emerging';
    return 'recurring';
  };

  const balance = describeTrend(
    countGroup(recent, ['aft_balance', 'lateral_balance', 'back_foot_dominance', 'stiff_ankles', 'centered_stance', 'stacked_alignment']),
    countGroup(older, ['aft_balance', 'lateral_balance', 'back_foot_dominance', 'stiff_ankles', 'centered_stance', 'stacked_alignment'])
  );
  const turnShape = describeTrend(
    countGroup(recent, ['inconsistent_turn_shape', 'unfinished_turn', 'open_turns', 'rounder_turn_shape', 'inconsistent_track']),
    countGroup(older, ['inconsistent_turn_shape', 'unfinished_turn', 'open_turns', 'rounder_turn_shape', 'inconsistent_track'])
  );
  const confidence = describeTrend(
    countGroup(recent, ['confidence_and_control', 'comfort_on_blue_groomers', 'comfort_on_green_groomers', 'comfort_on_red_terrain', 'calm_board_flow', 'terrain_adaptation_confidence']),
    countGroup(older, ['confidence_and_control', 'comfort_on_blue_groomers', 'comfort_on_green_groomers', 'comfort_on_red_terrain', 'calm_board_flow', 'terrain_adaptation_confidence'])
  );
  const edgeControl = describeTrend(
    countGroup(recent, ['late_edge_change', 'limited_edge_commitment', 'late_transition', 'smoother_edge_change', 'stronger_edge_commitment']),
    countGroup(older, ['late_edge_change', 'limited_edge_commitment', 'late_transition', 'smoother_edge_change', 'stronger_edge_commitment'])
  );
  const steering = describeTrend(
    countGroup(recent, ['counter_rotation', 'weak_lower_body_steering', 'lower_body_steering']),
    countGroup(older, ['counter_rotation', 'weak_lower_body_steering', 'lower_body_steering'])
  );

  return Object.fromEntries(
    Object.entries({
      balance,
      turn_shape: turnShape,
      confidence,
      edge_control: edgeControl,
      steering,
    }).filter(([, value]) => value)
  );
}

function resolveProgressAnalysisOptions(optionsOrFeedbackMap = {}) {
  if (
    optionsOrFeedbackMap &&
    !Array.isArray(optionsOrFeedbackMap) &&
    !Object.prototype.hasOwnProperty.call(optionsOrFeedbackMap, 'feedbackMap')
  ) {
    return { feedbackMap: optionsOrFeedbackMap };
  }

  return {
    feedbackMap: optionsOrFeedbackMap?.feedbackMap || {},
  };
}

export function aggregateProgressFromReviews(videos = [], optionsOrFeedbackMap = {}) {
  const { feedbackMap } = resolveProgressAnalysisOptions(optionsOrFeedbackMap);
  const reviewedVideos = (videos || [])
    .filter((video) => video?.status === 'reviewed')
    .sort((a, b) => getReviewTimestamp(b) - getReviewTimestamp(a));

  const recentReviews = reviewedVideos.slice(0, 5);
  const issueCounts = new Map();
  const goalCounts = new Map();
  const strengthCounts = new Map();
  const focusAreaCounts = new Map();
  const skillCounts = new Map();
  const drillCounts = new Map();
  const terrainCounts = new Map();
  const signalHistory = [];

  recentReviews.forEach((video) => {
    const timestamp = getReviewTimestamp(video);
    const normalizedAnalysis = normalizeReviewAnalysis(video, video.analysis);
    const signals = getAnalysisSignals(normalizedAnalysis);

    if (signals) {
      const issueKeys = [];
      const goalKeys = [];
      const strengthKeys = [];

      signals.issues.forEach((item) => {
        issueKeys.push(item.key);
        incrementCount(issueCounts, item.key, { label: item.label, amount: 1, lastSeenAt: timestamp });
        incrementCount(focusAreaCounts, item.key, { label: item.label, amount: 2, lastSeenAt: timestamp });
      });

      signals.goals.forEach((item) => {
        goalKeys.push(item.key);
        incrementCount(goalCounts, item.key, { label: item.label, amount: 1, lastSeenAt: timestamp });
        incrementCount(focusAreaCounts, item.key, { label: item.label, amount: 2, lastSeenAt: timestamp });
      });

      signals.strengths.forEach((item) => {
        strengthKeys.push(item.key);
        incrementCount(strengthCounts, item.key, { label: item.label, amount: 1, lastSeenAt: timestamp });
      });

      if (signals.terrainComfort) {
        incrementCount(terrainCounts, signals.terrainComfort, {
          label: signals.terrainComfort.replace(/_/g, ' '),
          amount: 1,
          lastSeenAt: timestamp,
        });
      }
      if (!signals.terrainComfort && signals.metadata?.terrainFocus) {
        incrementCount(terrainCounts, signals.metadata.terrainFocus, {
          label: signals.metadata.terrainFocus.replace(/_/g, ' '),
          amount: 1,
          lastSeenAt: timestamp,
        });
      }

      signalHistory.push({
        issueKeys,
        goalKeys,
        strengthKeys,
      });
    }

    // Pass 77 — the snowboard AI-rationale path (aiReview.suggestedDrillDetails / knowledgeMatchDetails →
    // recurringIssues/goals/strengths/focusAreas/trend) was REMOVED. AI is non-authoritative for progression;
    // these aggregates are HUMAN-only (video.analysis signals + human reviewDrills/focusSkills) and are used for
    // DISPLAY context, not for the canonical ranking (which lives in progressionEvidence.js).

    deriveFocusSkills(video, feedbackMap[video.id] || []).forEach((skill) => {
      incrementCount(skillCounts, skill.skillId, {
        label: skill.name,
        amount: skill.source === 'instructor_selected' ? 3 : skill.score,
        lastSeenAt: timestamp,
      });
      incrementCount(focusAreaCounts, skill.skillId, {
        label: skill.name,
        amount: skill.source === 'instructor_selected' ? 3 : 2,
        lastSeenAt: timestamp,
      });
    });

    (video.reviewDrills || []).forEach((drill) => {
      incrementCount(drillCounts, drill.id || drill.title, {
        label: drill.title,
        amount: 1,
        lastSeenAt: timestamp,
      });
    });
  });

  const aggregate = {
    reviewCount: reviewedVideos.length,
    recentReviewCount: recentReviews.length,
    recurringIssues: toSortedList(issueCounts, 4),
    developmentGoals: toSortedList(goalCounts, 4),
    strengths: toSortedList(strengthCounts, 4),
    focusAreas: toSortedList(focusAreaCounts, 4),
    topSkills: toSortedList(skillCounts, 4),
    topDrills: toSortedList(drillCounts, 4),
    terrainComfort: {
      bestKnown: toSortedList(terrainCounts, 1)[0]?.label || '',
      workingOn: toSortedList(terrainCounts, 2)[1]?.label || '',
    },
    trendSignals: buildTrendSignals(signalHistory),
  };

  return {
    ...aggregate,
    currentLevelSummary: buildCurrentLevelSummary(recentReviews[0], aggregate),
    hasProgressData:
      recentReviews.length > 0 &&
      (
        aggregate.recurringIssues.length > 0 ||
        aggregate.developmentGoals.length > 0 ||
        aggregate.strengths.length > 0 ||
        aggregate.topSkills.length > 0 ||
        aggregate.topDrills.length > 0
      ),
  };
}
